import { Component } from '@theme/component';

/**
 * Rotates the Satislent announcement band messages with a crossfade
 * (CSS handles the fade via the `is-active` class; this component only
 * schedules which message is active).
 *
 * - Pauses while hovered or focused so messages stay readable/clickable.
 * - Starts paused when the user prefers reduced motion (the toggle
 *   button still allows manual playback).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [toggle]
 *
 * @extends {Component<Refs>}
 */
class SatislentTicker extends Component {
  /** @type {number | null} */
  #timer = null;
  #index = 0;
  /** @type {HTMLElement[]} */
  #items = [];
  /** Sticky user/preference pause, as opposed to the transient hover pause */
  #paused = false;

  connectedCallback() {
    super.connectedCallback();

    this.#items = Array.from(this.querySelectorAll('.satislent-ticker__item'));
    if (this.#items.length === 0) return;

    this.#items[0].classList.add('is-active');
    if (this.#items.length < 2) return;

    this.refs.toggle?.addEventListener('click', this.#togglePlayback);

    this.#paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#syncToggle();
    if (!this.#paused) this.#start();

    this.addEventListener('pointerenter', this.#stop);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#stop);
    this.addEventListener('focusout', this.#resume);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  get #intervalMs() {
    const seconds = Number(this.dataset.interval);
    return (seconds > 0 ? seconds : 6) * 1000;
  }

  #start = () => {
    this.#stop();
    this.#timer = window.setInterval(this.#advance, this.#intervalMs);
  };

  #stop = () => {
    if (this.#timer !== null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  };

  #resume = () => {
    if (!this.#paused) this.#start();
  };

  #advance = () => {
    this.#items[this.#index]?.classList.remove('is-active');
    this.#index = (this.#index + 1) % this.#items.length;
    this.#items[this.#index]?.classList.add('is-active');
  };

  #togglePlayback = () => {
    this.#paused = !this.#paused;
    if (this.#paused) {
      this.#stop();
    } else {
      this.#start();
    }
    this.#syncToggle();
  };

  #syncToggle = () => {
    const { toggle } = this.refs;
    if (!toggle) return;

    toggle.setAttribute('aria-pressed', String(this.#paused));
    toggle.setAttribute('aria-label', toggle.dataset[this.#paused ? 'labelPlay' : 'labelPause'] ?? '');
  };
}

if (!customElements.get('satislent-ticker')) {
  customElements.define('satislent-ticker', SatislentTicker);
}
